import type { World } from './types';
import { TAG_HABITATS, renameTag, tagAnimal, tagGroup, type TagHabitat, type TagSpecies } from './tags';
import type { SiteId } from './data/sites';
import { LIMIT_GROUPS, LIMIT_NAMES, LIMIT_OPTIONS, PATROL_LEVELS, islandIndex, limitWords, patrolHoursAdded, takenAnywayOn, type LimitGroup } from './management';
import { REEF_ISLANDS, type ReefIsland } from './reef';
import { SURVEY_NAMES, boatName, checkedRule, dollars, hire, keepPlanInHeat, letGo, orderBoat, patrolCost, planSurvey, proposeRule, replan, ruleWords, sameTarget, startCommunity, startSurvey, stopCommunity, type RuleChange } from './operations';
import { ISLAND_NAMES } from './reef';
import { epochDayOf, dateLabel } from './calendar';
import { ECONOMY, ROLE_NAMES, type Role, type SurveyKind } from './economy';
import { MARKS, markSeen, readObjectives, skipGuide, type Mark } from './campaign';

/**
 * Every player action goes through here.
 *
 * Commands are the only way the outside world may change the simulation. That
 * boundary is what keeps the game deterministic and replayable: a world is a
 * pure function of its seed and the commands applied to it, in order, between
 * ticks. The interface builds a command and reads the result; it never writes
 * to the world.
 *
 * A command that needs randomness draws on a stream of its own that no
 * other system reads: a tagging's (M148) on the tags' (`STREAM.tags`), by
 * the tag's id and the day, so tagging moves no other draw.
 */

export type Command =
  | { type: 'rename_conservancy'; name: string }
  | { type: 'rename_sanctuary'; sanctuaryId: number; name: string }
  /** Tag one of a population's animals (a reef stock's on an island), an adult or a young one, and name it (M148); the drawn animal it was tagged from, where one was followed (M203). */
  | { type: 'tag_animal'; siteId: SiteId; species: TagSpecies; island?: number; stage: 'adult' | 'young'; name: string; groupId?: number; minLengthMm?: number; seenAs?: number; seenIn?: TagHabitat; cls?: number }
  /** Name a school of a reef stock on an island, or a pod its animals are tagged into (M148); the drawn school it was named from (M203). */
  | { type: 'tag_group'; siteId: SiteId; kind: 'school' | 'pod'; species: TagSpecies; island?: number; name: string; seenAs?: number }
  /** Rename a tagged animal or group (M148). */
  | { type: 'rename_tag'; id: number; name: string }
  /**
   * Protect an island's herbivores, or lift the protection (M165): no taking parrotfish, kala, nenue or manini (M178) on its reef, a policy
   * developed with its communities and established through the relevant authorities (the user's framing).
   */
  | { type: 'protect_herbivores'; sanctuaryId: number; island: string; protect: boolean }
  /** A fish group's daily bag limit or minimum size (M170): an option of `LIMIT_OPTIONS`, 0 Hawaiʻi's rules. */
  | { type: 'set_limit'; sanctuaryId: number; group: string; limit: 'bag' | 'size'; option: number }
  /** An island's patrols (M171): a level of `PATROL_LEVELS`, 0 Hawaiʻi's coverage alone; free until the budget (the user's word); for a term of days (`ECONOMY.patrolTerms`, M274), or until stopped where none is given. */
  | { type: 'set_patrol'; sanctuaryId: number; island: string; level: number; days?: number | null }
  /** Send the boat on a survey of a habitat, for a number of field days, its crew named (M238, `operations.ts`), to an island or the whole sanctuary (M274). */
  | { type: 'start_survey'; kind: SurveyKind; days: number; crew: number[]; island?: ReefIsland | null }
  /** Plan a survey (M274): sent at once where the boat is free, else put in the boat's plan, last or first; its crew named when it leaves. */
  | { type: 'plan_survey'; kind: SurveyKind; island: ReefIsland | null; days: number; biologists: number; first?: boolean }
  /** Take a planned survey out of the boat's plan, or put it first (M274). */
  | { type: 'replan'; id: number; to: 'cancel' | 'first' }
  /** In a heatwave, keep the boat's plan rather than assess the bleaching (M274; a bleaching assessment planned is the other choice). */
  | { type: 'keep_plan_in_heat' }
  /** Order the second boat (M277). */
  | { type: 'order_boat' }
  /** Set up, or end, an island's volunteer monitoring (M277). */
  | { type: 'start_volunteers'; island: ReefIsland }
  | { type: 'stop_volunteers'; island: ReefIsland }
  /** Propose a State rule: the liaison's consultation with the island's communities, then the State's rule-making (M238). */
  | { type: 'propose_rule'; rule: RuleChange }
  /** Hire one more of a role, or let a staff member go (M238). */
  | { type: 'hire'; role: Role }
  | { type: 'let_go'; staffId: number }
  /** What the player has looked at, for the campaign's objectives (M239): the Reef explored, the coral's monitoring and the threats read. */
  | { type: 'campaign_mark'; mark: Mark }
  | { type: 'skip_guide' };

export interface CommandResult {
  ok: boolean;
  message: string;
}

/**
 * DECISION: long enough for any real place name. The top bar shortens a long
 * one with an ellipsis rather than losing its buttons (review M14).
 */
export const MAX_NAME_LENGTH = 48;

export function applyCommand(world: World, command: Command): CommandResult {
  if (world.gameOver) return fail('The conservancy is no longer operating.');
  const result = dispatch(world, command);
  // The campaign's objectives read again (M239's review: the objective card waited for the next day).
  if (result.ok) readObjectives(world);
  return result;
}

function dispatch(world: World, command: Command): CommandResult {
  switch (command.type) {
    case 'rename_conservancy':
      return renameConservancy(world, command.name);
    case 'rename_sanctuary':
      return renameSanctuary(world, command.sanctuaryId, command.name);
    case 'tag_animal': {
      const name = cleanName(command.name);
      if (typeof name !== 'string') return name;
      const group = command.groupId === undefined ? null : world.tags.groups.find((g) => g.id === command.groupId);
      if (group === undefined) return fail('There is no such group.');
      if (command.minLengthMm !== undefined && !(command.minLengthMm >= 0)) return fail('It could not be tagged: no such size.');
      if (command.seenAs !== undefined && !Number.isInteger(command.seenAs)) return fail('It could not be tagged: no such animal drawn.');
      if (command.seenIn !== undefined && !TAG_HABITATS.includes(command.seenIn)) return fail('It could not be tagged: no such habitat.');
      if (command.cls !== undefined && !(Number.isInteger(command.cls) && command.cls >= 0)) return fail('It could not be tagged: no such age.');
      if (group && (group.kind !== 'pod' || group.species !== command.species || group.siteId !== command.siteId)) return fail('An animal joins a pod of its own kind and place.');
      const a = tagAnimal(world, { ...command, name });
      return typeof a === 'string' ? fail(`It could not be tagged: ${a}.`) : ok(`${name} is tagged.`);
    }
    case 'tag_group': {
      const name = cleanName(command.name);
      if (typeof name !== 'string') return name;
      if (command.seenAs !== undefined && !Number.isInteger(command.seenAs)) return fail('It could not be named: no such school drawn.');
      const g = tagGroup(world, { ...command, name });
      return typeof g === 'string' ? fail(`It could not be named: ${g}.`) : ok(`The ${command.kind} is now ${name}.`);
    }
    // A rule set at once (the science's scripts' and the tests' way; the game proposes it), never beside a proposal for
    // the same thing under way (review 29: it went round the consultation, and the proposal's end found nothing to make).
    case 'protect_herbivores':
      if (world.operations.jobs.some((j) => j.rule && sameTarget(j.rule, { type: 'protect_herbivores', island: command.island as ReefIsland, protect: command.protect }))) return fail('A proposal for it is under way: its consultation and the State decide it.');
      return protectHerbivores(world, command.sanctuaryId, command.island, command.protect);
    case 'set_limit':
      if (world.operations.jobs.some((j) => j.rule && sameTarget(j.rule, { type: 'set_limit', group: command.group as LimitGroup, limit: command.limit, option: command.option }))) return fail('A proposal for it is under way: its consultation and the State decide it.');
      return setLimit(world, command.sanctuaryId, command.group, command.limit, command.option);
    case 'set_patrol':
      return setPatrol(world, command.sanctuaryId, command.island, command.level, command.days ?? null);
    case 'start_survey': {
      if (!Array.isArray(command.crew) || !command.crew.every(Number.isInteger)) return fail('There is no such crew.');
      const job = startSurvey(world, command.kind, command.days, command.crew, command.island ?? null);
      return typeof job === 'string' ? fail(`The survey cannot go: ${job}.`)
        : ok(`The ${boatName(job.boat)} sails on a ${SURVEY_NAMES[command.kind].toLowerCase()}${job.island ? ` to ${ISLAND_NAMES[job.island]}` : ''}, ${command.days} days at sea, for ${dollars(job.cost)}.`);
    }
    case 'plan_survey': {
      const r = planSurvey(world, { kind: command.kind, island: command.island ?? null, days: command.days, biologists: command.biologists, first: command.first === true });
      if (typeof r === 'string') return fail(`The survey cannot be planned: ${r}.`);
      const where = command.island ? ` to ${ISLAND_NAMES[command.island]}` : '';
      return 'phaseEnds' in r ? ok(`The ${boatName(r.boat)} sails on a ${SURVEY_NAMES[command.kind].toLowerCase()}${where}, ${command.days} days at sea, for ${dollars(r.cost)}.`)
        : ok(`Planned: a ${SURVEY_NAMES[command.kind].toLowerCase()}${where}, ${command.first ? 'first' : 'next in turn'} when ${world.operations.boats > 1 ? 'a boat and its crew are' : `the ${ECONOMY.boat.name} and its crew are`} free.`);
    }
    case 'replan': {
      const r = replan(world, command.id, command.to);
      return typeof r === 'string' ? fail(`It cannot be changed: ${r}.`) : ok(command.to === 'cancel' ? `The ${SURVEY_NAMES[r.kind].toLowerCase()} is taken out of the plan.` : `The ${SURVEY_NAMES[r.kind].toLowerCase()} goes first when ${world.operations.boats > 1 ? 'a boat' : `the ${ECONOMY.boat.name}`} is free.`);
    }
    case 'order_boat': {
      const p = orderBoat(world);
      return typeof p === 'string' ? fail(`It cannot be ordered: ${p}.`) : ok(`The ${ECONOMY.secondBoat.name} is ordered, ${dollars(p.cost)}: in the water ${dateLabel(p.readyDay)}. Hire a second captain to run her.`);
    }
    case 'start_volunteers': {
      const j = startCommunity(world, command.island);
      return typeof j === 'string' ? fail(`It cannot be set up: ${j}.`) : ok(`Your liaison sets up ${ISLAND_NAMES[command.island]}'s volunteers with its community, a month; they count from ${dateLabel(j.phaseEnds[0] as number)}.`);
    }
    case 'stop_volunteers': {
      const why = stopCommunity(world, command.island);
      return why ? fail(`It cannot be ended: ${why}.`) : ok(`${ISLAND_NAMES[command.island]}'s volunteers stop counting.`);
    }
    case 'keep_plan_in_heat': {
      const why = keepPlanInHeat(world);
      return why ? fail(`It cannot be chosen: ${why}.`) : ok(`${world.operations.boats > 1 ? 'The boats keep to their' : `The ${ECONOMY.boat.name} keeps to its`} plan: the heat's toll will be known from the next survey of the coral.`);
    }
    case 'propose_rule': {
      const rule = checkedRule(command.rule);
      if (typeof rule === 'string') return fail(`It cannot be proposed: ${rule}.`);
      const job = proposeRule(world, rule);
      return typeof job === 'string' ? fail(`It cannot be proposed: ${job}.`)
        : ok(`${ruleWords(rule)}: its consultation begins, about ${Math.round(ECONOMY.rule.consultationDays / 30)} months with the island's communities, then the State's rule-making.`);
    }
    case 'hire': {
      const m = hire(world, command.role);
      return typeof m === 'string' ? fail(`No one can be hired: ${m}.`) : ok(`${m.name} joins as a ${ROLE_NAMES[m.role].toLowerCase()}, at ${dollars(ECONOMY.monthlyPay[m.role])} a month.`);
    }
    case 'let_go': {
      const m = letGo(world, command.staffId);
      return typeof m === 'string' ? fail(`They cannot be let go: ${m}.`) : ok(`${m.name} leaves the conservancy, paid to today.`);
    }
    case 'skip_guide':
      return skipGuide(world) ? ok('The guide is put away: the sanctuary\'s goals lead from here.') : fail('The guide is already done.');
    case 'campaign_mark':
      if (!(MARKS as readonly string[]).includes(command.mark)) return fail('There is no such mark.');
      return markSeen(world, command.mark) ? ok('Noted.') : fail('Already noted.');
    case 'rename_tag': {
      const name = cleanName(command.name);
      if (typeof name !== 'string') return name;
      return renameTag(world, command.id, name) ? ok(`It is now ${name}.`) : fail('There is no such tag.');
    }
    default: {
      // A command from a newer build, a replay or a bug in the interface: refused
      // by name rather than ignored, so the caller sees it.
      const unknown = command as { type?: unknown };
      return fail(`Unknown command: ${String(unknown.type)}.`);
    }
  }
}

function renameConservancy(world: World, name: string): CommandResult {
  const clean = cleanName(name);
  if (typeof clean !== 'string') return clean;
  world.conservancyName = clean;
  return ok(`The conservancy is now ${clean}.`);
}

function renameSanctuary(world: World, sanctuaryId: number, name: string): CommandResult {
  const sanctuary = world.sanctuaries.find((s) => s.id === sanctuaryId);
  if (!sanctuary) return fail('There is no such sanctuary.');
  const clean = cleanName(name);
  if (typeof clean !== 'string') return clean;
  sanctuary.name = clean;
  return ok(`The sanctuary is now ${clean}.`);
}

function protectHerbivores(world: World, sanctuaryId: number, island: string, protect: boolean): CommandResult {
  const sanctuary = world.sanctuaries.find((s) => s.id === sanctuaryId);
  if (!sanctuary) return fail('There is no such sanctuary.');
  if (!sanctuary.management) return fail('This sanctuary has no reef fish to protect.');
  const i = islandIndex(island);
  if (i === null) return fail('There is no such island at this sanctuary.');
  if (typeof protect !== 'boolean') return fail('Protect or lift: no such choice.');
  if (sanctuary.management.herbivoreProtection[i] === protect) return fail(protect ? 'Its herbivores are already protected.' : 'Its herbivores are not protected.');
  sanctuary.management.herbivoreProtection[i] = protect;
  return ok(protect
    ? 'Developed with the island\'s communities and established through the State, its Herbivore Protection Area takes effect: no parrotfish, kala, nenue or manini may be taken on its reef.'
    : 'The island\'s Herbivore Protection Area is lifted: its parrotfish, kala, nenue and manini may be taken again.');
}

function setLimit(world: World, sanctuaryId: number, group: string, limit: string, option: number): CommandResult {
  const sanctuary = world.sanctuaries.find((s) => s.id === sanctuaryId);
  if (!sanctuary) return fail('There is no such sanctuary.');
  if (!sanctuary.management) return fail('This sanctuary has no reef fish to manage.');
  if (!(LIMIT_GROUPS as readonly string[]).includes(group)) return fail('There is no such fish group.');
  const g = group as LimitGroup;
  if (limit !== 'bag' && limit !== 'size') return fail('A bag limit or a size limit: no such limit.');
  const options = limit === 'bag' ? LIMIT_OPTIONS[g].bags : LIMIT_OPTIONS[g].sizes;
  if (!Number.isInteger(option) || option < 0 || option >= options.length) return fail('There is no such choice.');
  if (sanctuary.management.limits[g][limit] === option) return fail('That rule is already in force.');
  sanctuary.management.limits[g][limit] = option;
  return ok(option === 0 ? `Hawaiʻi's own ${limit === 'bag' ? 'bag limit' : 'minimum size'} holds again for the ${LIMIT_NAMES[g]}.`
    : `The sanctuary's ${limit === 'bag' ? 'bag limit' : 'minimum size'} for the ${LIMIT_NAMES[g]} takes effect on all four islands: ${limitWords(g, limit, option)}.`);
}

function setPatrol(world: World, sanctuaryId: number, island: string, level: number, days: number | null): CommandResult {
  const sanctuary = world.sanctuaries.find((s) => s.id === sanctuaryId);
  if (!sanctuary) return fail('There is no such sanctuary.');
  if (!sanctuary.management) return fail('This sanctuary has no reef fish to patrol for.');
  const i = islandIndex(island);
  if (i === null) return fail('There is no such island at this sanctuary.');
  if (!Number.isInteger(level) || level < 0 || level >= PATROL_LEVELS.length) return fail('There is no such patrol level.');
  if (days !== null && !ECONOMY.patrolTerms.includes(days)) return fail('There is no such term for a patrol.');
  if (level === 0 && days !== null) return fail('Hawaiʻi\'s officers alone need no term.');
  // Its days from tomorrow's (the next tick's fishing the first it keeps), its last paid for and kept.
  const until = days === null ? null : epochDayOf(world) + days;
  if (sanctuary.management.patrols[i] === level && sanctuary.management.patrolUntil[i] === until) return fail('Its patrols are already at that level.');
  // Officers' hours are bought (M238): none more while the conservancy is in debt.
  if (level > (sanctuary.management.patrols[i] ?? 0) && world.operations.funds < 0) return fail('The conservancy is in debt: it can pay for no more officers\' hours.');
  sanctuary.management.patrols[i] = level;
  sanctuary.management.patrolUntil[i] = until;
  // Told as a month's cost: by definition 30 days, the button's unit.
  const cost = patrolCost(sanctuary, 30);
  const where = REEF_ISLANDS[i] as ReefIsland;
  const term = until === null ? 'until stopped' : `to ${dateLabel(until)}`;
  return ok(level === 0 ? `The island's reef is patrolled by Hawaiʻi's officers alone again; the sanctuary's patrols now cost about ${dollars(cost)} a month.`
    : `${ISLAND_NAMES[where]}'s reef is patrolled ${PATROL_LEVELS[level]?.name}, ${term}, about ${String(Math.round(patrolHoursAdded(where, level) / 100) * 100).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} officers' hours a year beside Hawaiʻi's: about ${Math.round(100 * takenAnywayOn(where, level))}% of what a rule forbids still taken, against ${Math.round(100 * takenAnywayOn(where, 0))}%; the sanctuary's patrols now cost about ${dollars(cost)} a month.`);
}

/** Whether a name is as a command keeps one (a save's names, M148). */
export const nameKept = (name: string): boolean => cleanName(name) === name;

/** The name trimmed, or the refusal. */
function cleanName(name: string): string | CommandResult {
  const clean = name.trim().replace(/\s+/g, ' ');
  if (clean.length === 0) return fail('A name cannot be empty.');
  if (clean.length > MAX_NAME_LENGTH) return fail(`A name can be at most ${MAX_NAME_LENGTH} characters.`);
  return clean;
}

function ok(message: string): CommandResult {
  return { ok: true, message };
}

function fail(message: string): CommandResult {
  return { ok: false, message };
}
